import { useEffect } from 'react';
import {
  ComponentPropsContext,
  DictionaryPhrases,
  Field,
  SitecoreContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';

import {
  DEFAULT_SC_LOCALE,
  FeaturesSettings,
  getFallbackLocale,
  getFullSiteSettings,
  getLocaleWithRegion,
  getSiteFeatures,
  SiteSettings,
  transformResponseUrls,
} from '@gn-audio/jss-core';

import {
  fetchCartDictionaryREST,
  fetchJSSDictionary,
  fetchSystemDictionaryREST,
  fetchTopMenuDictionaryREST,
  fetchUipDictionaryREST,
} from 'api/dictionaryApi';
import { getFamilyGroups } from 'api/productCatalogueApi';
import { fetchThemes, JabraSitecoreTheme } from 'api/themeQueries';
import { AppSettingFields } from 'common/appSettings/appSettings.model';
import { fetchAppSettings } from 'common/appSettings/appSettingsService';
import { configurePricingApi, initializeHttpClients } from 'common/core-components/bootstrapApis';
import { getSharedProperty } from 'common/getters';
import { parseSCLookupValue } from 'common/siteCoreHelpers';
import { PublicEnvironmentConfigs } from 'common/types/commonTypes';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { sitemapFetcher } from 'lib/sitemap-fetcher';
import Layout, { RouteFields } from 'src/Layout';
import NotFound from 'src/NotFound';
import { componentBuilder } from 'temp/componentBuilder';
import getConfig from 'temp/config';
import { getRedirectInfo } from 'utils/redirectUtils';

const SitecorePage = ({
  componentProps,
  layoutData,
  headLinks,
  publicUrl,
}: SitecorePageProps): JSX.Element => {
  useEffect(() => {
    // Only refresh editor fast refresh in development mode
    if (process.env.NODE_ENV === 'development') {
      handleEditorFastRefresh();
    }
  }, []);

  if (!layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  {
    /*
          Sitecore Pages supports component rendering to avoid refreshing the entire page during component editing.
          If you are using Experience Editor only, this logic can be removed, Layout can be left.
        */
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout layoutData={layoutData} headLinks={headLinks} publicUrl={publicUrl} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (process.env.NODE_ENV !== 'development' && !process.env.DISABLE_SSG_FETCH) {
    try {
      // Note: Next.js runs export in production mode
      paths = await sitemapFetcher.fetch(context);
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

export type JabraPageProps = SitecorePageProps & {
  envConfig: PublicEnvironmentConfigs;
  jabraDictionary?: Record<string, unknown>;
  themes: JabraSitecoreTheme[];
  error?: unknown;
  appSettings?: AppSettingFields | null;
  publicUrl: string;
  siteSettings: SiteSettings;
  features: FeaturesSettings;
};

type CategorizedDictionaryPhrases =
  | DictionaryPhrases
  | {
      cart: DictionaryPhrases;
      system: DictionaryPhrases;
      uip: DictionaryPhrases;
      jss: DictionaryPhrases;
      topmenu: DictionaryPhrases;
    };

const REVALIDATE_INTERVAL = process.env.ISR_REVALIDATE_INTERVAL
  ? parseInt(process.env.ISR_REVALIDATE_INTERVAL, 10)
  : 60;

const fetchDictionaries = async (
  locale: string,
  sysDictionary: DictionaryPhrases,
  fallbackLocale: string = DEFAULT_SC_LOCALE
) => {
  const language = getLocaleWithRegion(locale);

  try {
    const [cart, uip, system, topmenu, jss] = await Promise.all([
      fetchCartDictionaryREST(language),
      fetchUipDictionaryREST(language),
      fetchSystemDictionaryREST(language),
      fetchTopMenuDictionaryREST(language),
      fetchJSSDictionary(locale, fallbackLocale),
    ]);

    return { ...sysDictionary, cart, system, uip, jss, topmenu };
  } catch (error) {
    console.error('Error fetching dictionaries:', error);
    return sysDictionary; // Return system dictionary if others fail
  }
};

const fetchSiteConfiguration = async (envConfig: PublicEnvironmentConfigs, locale: string) => {
  const language = getLocaleWithRegion(locale);

  initializeHttpClients(envConfig);

  const [appSettings, siteSettings, features] = await Promise.all([
    fetchAppSettings(locale),
    getFullSiteSettings(language),
    getSiteFeatures(language),
  ]);

  if (siteSettings.sfcc) {
    configurePricingApi(siteSettings.sfcc, features.SfccPrice ?? false, language);
  }

  return { appSettings, siteSettings, features };
};

const handleRedirects = (context: GetStaticPropsContext, fields: RouteFields, config: any) => {
  const redirectResult = getRedirectInfo(
    context.locale,
    fields,
    REVALIDATE_INTERVAL,
    config.defaultLanguage
  );

  return redirectResult;
};

const handleNotFoundPage = async (
  context: GetStaticPropsContext,
  dictionaryPhrases: CategorizedDictionaryPhrases,
  envConfig: PublicEnvironmentConfigs
) => {
  try {
    const notFoundRenderingProps = await sitecorePagePropsFactory.create({
      ...context,
      params: { ...context.params, path: '/_404' },
    });

    const transformedProps = transformResponseUrls(
      notFoundRenderingProps,
      context.locale === context.defaultLocale ? undefined : context.locale
    );

    return {
      props: {
        ...transformedProps,
        dictionaryPhrases,
        envConfig,
      },
      revalidate: REVALIDATE_INTERVAL,
    };
  } catch (error) {
    console.error('Error fetching not found page:', error);
    throw error;
  }
};

const isProductUnavailable = async (
  productFamilyId: string,
  locale: string | undefined
): Promise<boolean> => {
  if (!productFamilyId) {
    return false;
  }
  const language = getLocaleWithRegion(locale);
  const familyProductData = await getFamilyGroups(parseSCLookupValue(productFamilyId).id, language);
  return !familyProductData;
};

const handleUnavailableProduct = async (
  context: GetStaticPropsContext,
  dictionaryPhrases: CategorizedDictionaryPhrases,
  envConfig: PublicEnvironmentConfigs,
  themes: JabraSitecoreTheme[],
  siteConfiguration: any,
  productFamilyId: string
) => {
  const isUnavailable = await isProductUnavailable(productFamilyId, context.locale);

  if (isUnavailable) {
    const notAvailableRenderingProps = await sitecorePagePropsFactory.create({
      ...context,
      params: { ...context.params, path: '/product-not-available' },
    });

    const transformedProps = transformResponseUrls(
      notAvailableRenderingProps,
      context.locale === context.defaultLocale ? undefined : context.locale
    );

    return {
      props: {
        ...transformedProps,
        dictionaryPhrases,
        envConfig,
        themes,
        ...siteConfiguration,
      },
      revalidate: REVALIDATE_INTERVAL,
    };
  }

  return null;
};

export const getStaticProps: GetStaticProps<JabraPageProps> = async (context) => {
  const config = getConfig();
  const envConfig: PublicEnvironmentConfigs = {
    siteSettingsApiBaseUrl: config.siteSettingsApiBaseUrl,
    productCatalogueBaseUrl: config.productCatalogueBaseUrl,
    productCatalogueApiKey: config.productCatalogueApiKey,
    marketingDocumentsBaseUrl: config.marketingDocumentsBaseUrl,
    knowledgebaseApiBaseUrl: config.knowledgebaseApiBaseUrl,
    publicUrl: config.publicUrl,
  };

  const siteConfiguration = await fetchSiteConfiguration(
    envConfig,
    context.locale ?? DEFAULT_SC_LOCALE
  );

  let props = await sitecorePagePropsFactory.create(context);

  try {
    const fields = props.layoutData.sitecore.route?.fields as RouteFields;
    const redirectResult = handleRedirects(context, fields, config);
    if (redirectResult) {
      return redirectResult;
    }
  } catch (error) {
    console.error('Redirect error:', error);
    throw error;
  }

  const fallbackLocale = context.locale
    ? getFallbackLocale(props.layoutData, context.locale)
    : context.defaultLocale;

  const themes = await fetchThemes();
  const jabraDictionary = await fetchDictionaries(
    context.locale ?? DEFAULT_SC_LOCALE,
    props.dictionary,
    fallbackLocale
  );

  let publicUrl: string = envConfig.publicUrl || '';

  try {
    if (props.notFound) {
      return await handleNotFoundPage(context, jabraDictionary, envConfig);
    }

    const productFamilyId = getSharedProperty(
      props.layoutData.sitecore.route?.fields,
      'Product'
    ) as Field<string>;

    if (productFamilyId?.value) {
      const unavailableProductResult = await handleUnavailableProduct(
        context,
        jabraDictionary,
        envConfig,
        themes,
        siteConfiguration,
        productFamilyId.value
      );
      if (unavailableProductResult) return unavailableProductResult;
    }

    const componentProps = transformResponseUrls(
      props,
      context.locale === context.defaultLocale ? undefined : context.locale
    );

    return {
      props: {
        ...componentProps,
        jabraDictionary,
        envConfig,
        themes,
        publicUrl,
        ...siteConfiguration,
      },
      revalidate: REVALIDATE_INTERVAL,
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    return {
      props: { ...props, envConfig, themes: [], ...siteConfiguration, error: 'Server Error' },
      revalidate: REVALIDATE_INTERVAL,
    };
  }
};

export default SitecorePage;
